/* Color-grouped PDP galleries (issue #466): show only the selected color's photos.

   WHY: a product like Novia carries 70 photos (5 colors x 14) in one reel, Pagosa
   3-piece 246. Pipeline 7.3 ships an alt-text version of this (GroupVariantImages,
   `#color_x` at the end of each alt), but it writes into the alt text screen readers
   speak, is undocumented for Pipeline, and is gone in 8.x. This groups by IMAGE ORDER
   instead: each color's variant image starts that color's block, and the block runs
   until the next color's variant image. Photos before the first block show for every
   color. Nothing in admin or in alt text changes.

   ENABLED per product by the theme setting "Color-grouped galleries" (Product form
   group): sections/product.liquid and snippets/add-to-cart-sticky.liquid add
   data-tc-color-groups to the gallery and load this file only for listed handles.
   A gallery without a Flickity slider at both breakpoints (Stacked/Grid/Mosaic
   desktop layout, or no phone slider) and a product whose data does not fit (no
   color option, a color with no variant image,
   two colors on one image, one color's variants on different images) is left alone.

   HOW: the other colors' slides are taken out of the Flickity slider with its own
   remove()/insert() API and parked in a hidden holder next to the gallery (still
   inside the product section, so theme.js's zoom wiring reaches their zoom buttons
   whenever it runs), so Flickity's cells, dots, wraparound and
   selected index only ever describe the shown color. Everything that reads the slider
   or the DOM follows without changes: the #451 counter and dots (14 dots at 375px are
   far enough apart to be tappable again), the #71 announcer ("Slide 1 of 14"),
   window.__tcGalleryExposureSync (#150, do-not-repatch; it re-runs on the childList
   mutation), and assets/tc-gallery-defer.js (its slide list and selected index are
   both per-color). theme.js recreates Flickity from the slider's children across the
   768px breakpoint, so a rebuilt instance holds the same color.

   Color changes arrive as theme.js's own `theme:image:change` (dispatched on the
   gallery by Media.changeProductImage on a variant change, and by thumbnail clicks),
   whose handler looks the media id up in flkty.cells and selects it. A capture-phase
   listener on document runs before that handler: when the id belongs to another
   color, it swaps the slides and selects the id instantly (no pan through photos the
   shopper did not ask for), so theme.js's select finds the same index and is a no-op.
   Each gallery is handled on its own: the sticky add-to-cart bar has its own product
   section, form and gallery, and follows its own events.

   FIRST PAINT (issue #475): the server now renders the grouping. Before, the HTML held
   every photo in catalog order, so until Flickity booted (about 2.7 s on a phone) the
   first color's lead photo was painted, preloaded and fetched at high priority, even
   for a ?variant= link to another color or a sold-out first color.
   snippets/tc-color-group-map.liquid applies the same rule as buildGroups() below, and
   sections/product.liquid / snippets/add-to-cart-sticky.liquid render shared photos
   plus the selected variant's color in the gallery (its lead photo is position 1:
   eager, preloaded, fetchpriority high), the other colors as deferred templates in the
   [data-tc-color-group-store] holder right after the gallery, other colors' thumbnails
   hidden, data-tc-color on every slide and data-tc-color-group on the gallery. This
   script adopts that state as is (no swap at boot). The bind ladder only catches a
   mismatch: a selected variant whose color differs from the rendered one (a browser
   restoring form state, a variant change before the slider existed).
   FALLBACK: slides without data-tc-color (HTML cached from before #475, or a product
   the server leaves to this script because a photo is a video or 3D model, see
   snippets/tc-color-group-plan.liquid) take the client path above unchanged:
   buildGroups() by DOM order and a swap once Flickity is built.

   RULE UPGRADES (issue #484), server path only: the server rule is now richer than
   buildGroups(), which keeps the older rule (one variant image per color, no shared
   callouts) for the fallback.
   - R1: variant images are found by media id, so 0-based media positions (Pagosa
     3-piece) group too.
   - R2: a color may have several variant images (bundles, one per combination with the
     other option). The server renders the selected variant's own image first in its
     color; a change of the second option stays inside the color and is theme.js's
     ordinary select by media id; a change of the first option swaps colors here.
   - R3: when every callout (spec, size and packing infographics, found by file name)
     sits in one color's block, the server marks them data-tc-color="" plus
     data-tc-color-shared="after": every color shows them after its own photos. apply()
     keeps the leading shared photos, the new color and these trailing shared photos in
     the slider, inserts the new color between the two shared sets, never parks the
     trailing ones, and moves their thumbnails after the new color's thumbnails.

   SWAP ORDER (issue #520): apply() inserts the new color BEFORE it removes the old one.
   Removing first emptied the slider on every product with no shared photo left in it
   (the bundles, Novia): Flickity then clamped selectedIndex to -1, and insert()
   re-selected -1, which wrapAround turned into the LAST slide of the new color and
   reported as a `change`. tc-gallery-defer.js answers `change` by hydrating that
   slide's neighbors and starting its photo at fetchpriority high, so a color tap
   downloaded a photo the shopper never picked, at high priority, in competition with
   the one they did (measured on live: MaxPorter "Navy / USA Olympics Flag", Novia's
   last photo). Inserting first keeps the selected slide in the slider: Flickity only
   re-indexes it, which emits no `change`, so selectMedia() sends the swap's only
   `change`, for the photo the shopper asked for.

   PRELOAD (issue #475): a swatch tap used to show a blank slide while the new color's
   lead photo downloaded (1.4 s at 1.6 Mbps). After `load` and an idle moment, each
   other color's lead photo (its variant image, the first slide of its block) is
   hydrated in the holder with loading="eager" fetchpriority="low", so it downloads
   while parked. Its srcset `sizes` are viewport media queries, so the parked image
   picks the same candidate it uses once shown: the tap reuses that download. Main
   gallery only (never the sticky bar's copy); skipped on Save-Data and 2g. Since issue
   #484 only colors with at least one available (in-stock) variant are preloaded: a
   12-color product would otherwise fetch up to 11 photos.
   Since issue #520, on a product with more than one option (the R2 bundles) the photo
   preloaded for a color is the one a tap on that color would show: the photo of the
   variant with that color and the chosen variant's other option values. A combination
   that does not exist or is sold out falls back to the color's first photo.
   TRICKLE (issues #520 and #526, multi-option products only): after every variant
   change the photos the shopper may want next download one at a time, at low priority,
   never while the photo on screen is still loading (each step re-checks that before it
   starts anything), never on Save-Data or 2g, never for sold-out combinations, main
   gallery only. Order:
   1. #526: the CURRENT color's other combination photos, nearest to the selected slide
      first (with wrap, ties forward), so a second-option tap finds its photo ready.
      Those slides sit in the slider, where tc-gallery-defer.js owns hydration (#140
      do-not-repatch). Only slides still holding their deferred template are taken: a
      detached Image with the template image's srcset and sizes (viewport media queries)
      downloads the same file into the browser cache without touching the slide. Once
      it is there, the template is swapped in (the same swap as tc-gallery-defer.js
      hydrateSlide(); measured: no cell, slider or layout-shift change), so the photo's
      300 ms fade-in runs while it is not shown. Hydrated slides are defer's +-2 window,
      which loads on its own.
   2. #520: each other color's next-tap photo for the current option values. A target
      an earlier visit hydrated as a neighbor but never loaded (lazy, kept off screen on
      a phone) counts as not yet downloaded and is switched to eager, low priority.
   Photos already downloaded stay, so each option state adds at most one photo per
   color. Single-option products keep the lead photo and the one preload pass.
   OPENING LEAD (issue #533, single-option products only): a gallery that opens on
   LEADING shared photos (Argo Pet) shows its own color's lead photo after them, outside
   tc-gallery-defer.js's +-2 window, so that slide kept its deferred template and a swap
   parked it undownloaded: going back to the opening color then downloaded its lead on
   demand (a blank slide for up to 2.8 s at 1.6 Mbps). The opening color's lead is
   captured once when the post-load pass is set up (Codex P2 on PR #535, comment
   4187046353: a shopper may change color while the preloads still download). After
   the one preload pass, once the shown photo and those preloads have finished, that
   slide is warmed wherever it is by then, if it is still a deferred template: in the
   slider through the #526 detached-Image path, then swapped in from the browser cache;
   parked in the holder through the same eager, low-priority preload the other colors'
   leads get. Never the selected slide; in-stock colors only; main gallery only; never
   on Save-Data or 2g; once per page. A gallery without leading shared photos opens on
   its lead, the selected slide 1, so nothing changes there.
   Multi-option products are untouched: their trickle already re-preloads the parked
   colors after every change.

   Known limit: the zoom lightbox (PhotoSwipe) builds its list from the product JSON
   inside theme.js, so it still steps through every color.

   Namespace: window.tcGalleryColorGroups. No dependencies; never edits theme.js. */
(function () {
  'use strict';
  if (window.tcGalleryColorGroups) return;

  var GALLERY_SELECTOR = '[data-product-slideshow][data-tc-color-groups]';
  var SLIDE_SELECTOR = '[data-media-slide]';
  var THUMB_SELECTOR = '[data-slideshow-thumbnail]';
  var SECTION_SELECTOR = '[data-section-type="product"]';
  var MEDIA_WRAPPER_SELECTOR = '.product__media__wrapper';
  var STORE_SELECTOR = '[data-tc-color-group-store]';
  var STICKY_COPY_SELECTOR = '[data-sticky-media-wrapper]';
  var TEMPLATE_SELECTOR = 'template[data-deferred-media]';
  var COLOR_ATTR = 'data-tc-color';
  var SHARED_ATTR = 'data-tc-color-shared'; // "after": a shared callout (#484 R3)
  var COLOR_OPTION = /colou?r/i;
  var BIND_RETRY_MS = 100;
  var BIND_RETRY_LIMIT = 100; // ~10s for theme.js to build the slider
  var PRELOAD_IDLE_TIMEOUT_MS = 2000;
  var PRELOAD_FALLBACK_MS = 1500;

  // Same lookup as tc-gallery-defer.js: vendor.js exposes the constructor on
  // window.themeVendor, never as a global.
  function flickityCtor() {
    var tv = window.themeVendor;
    var F = window.Flickity || (tv && tv.Flickity && (tv.Flickity.default || tv.Flickity));
    return F && typeof F.data === 'function' ? F : null;
  }

  function liveFlickity(gallery) {
    var F = flickityCtor();
    var live = F ? F.data(gallery) : null;
    return live && live.cells ? live : null;
  }

  function readProduct(section) {
    var node = (section && section.querySelector('[data-product-json]')) || document.querySelector('[data-product-json]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function optionName(option) {
    return typeof option === 'string' ? option : (option && option.name) || '';
  }

  // Returns { colorByMediaId, colors } or null when the product does not fit.
  function buildGroups(product, slides) {
    if (!product || !product.options || !product.variants || slides.length < 2) return null;
    var optionIndex = -1;
    for (var o = 0; o < product.options.length; o++) {
      if (COLOR_OPTION.test(optionName(product.options[o]))) { optionIndex = o; break; }
    }
    if (optionIndex < 0) return null;

    var positionById = {};
    slides.forEach(function (slide, i) { positionById[slide.getAttribute('data-media-id')] = i; });

    var startByColor = {};
    var colors = [];
    for (var v = 0; v < product.variants.length; v++) {
      var variant = product.variants[v];
      var color = variant.options ? variant.options[optionIndex] : variant['option' + (optionIndex + 1)];
      var media = variant.featured_media;
      if (color == null || !media || positionById[String(media.id)] === undefined) return null;
      var start = positionById[String(media.id)];
      if (Object.prototype.hasOwnProperty.call(startByColor, color)) {
        if (startByColor[color] !== start) return null; // one color, several images: ambiguous
        continue;
      }
      startByColor[color] = start;
      colors.push(color);
    }
    if (colors.length < 2) return null;
    colors.sort(function (a, b) { return startByColor[a] - startByColor[b]; });
    for (var c = 1; c < colors.length; c++) {
      if (startByColor[colors[c]] === startByColor[colors[c - 1]]) return null; // two colors, one image
    }

    var colorByMediaId = {};
    var k = -1;
    slides.forEach(function (slide, i) {
      while (k + 1 < colors.length && startByColor[colors[k + 1]] <= i) k++;
      colorByMediaId[slide.getAttribute('data-media-id')] = k < 0 ? '' : colors[k]; // '' = every color
    });
    return { colorByMediaId: colorByMediaId, colors: colors, startByColor: startByColor };
  }

  function indexOfMedia(flkty, id) {
    for (var i = 0; i < flkty.cells.length; i++) {
      if (flkty.cells[i].element.getAttribute('data-media-id') === String(id)) return i;
    }
    return -1;
  }

  function slidesIn(root) {
    return Array.prototype.slice.call(root.querySelectorAll(SLIDE_SELECTOR));
  }

  // Issue #475: the groups the server rendered, read from each slide's data-tc-color
  // ('' = every color). `slides` is the gallery's slides followed by its holder's; one
  // color's slides all sit in one of the two, in catalog order.
  function serverGroups(slides) {
    var colorByMediaId = {};
    var colors = [];
    slides.forEach(function (slide) {
      var color = slide.getAttribute(COLOR_ATTR) || '';
      colorByMediaId[slide.getAttribute('data-media-id')] = color;
      if (color && colors.indexOf(color) < 0) colors.push(color);
    });
    return colors.length >= 2 ? { colorByMediaId: colorByMediaId, colors: colors } : null;
  }

  function saveDataRequested() {
    var connection = navigator.connection;
    if (!connection) return false;
    return !!connection.saveData || connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g';
  }

  // Issue #484: the colors with at least one available variant, as { color: true }, or
  // null when the product JSON cannot tell (then every color is preloaded, as before).
  function availableColors(product) {
    if (!product || !product.options || !product.variants) return null;
    var optionIndex = -1;
    for (var o = 0; o < product.options.length; o++) {
      if (COLOR_OPTION.test(optionName(product.options[o]))) { optionIndex = o; break; }
    }
    if (optionIndex < 0) return null;
    var available = {};
    product.variants.forEach(function (variant) {
      var color = variant.options ? variant.options[optionIndex] : variant['option' + (optionIndex + 1)];
      if (variant.available && color != null) available[color] = true;
    });
    return available;
  }

  // Issue #520: the index of the color option (the first option whose name contains
  // color or colour, the rule availableColors() and tc-color-group-map.liquid use), or -1.
  function colorOptionIndex(product) {
    if (!product || !product.options) return -1;
    for (var o = 0; o < product.options.length; o++) {
      if (COLOR_OPTION.test(optionName(product.options[o]))) return o;
    }
    return -1;
  }

  function optionValue(variant, index) {
    return variant.options ? variant.options[index] : variant['option' + (index + 1)];
  }

  // The same swap as hydrateSlide() in tc-gallery-defer.js (the template's aspect-ratio
  // sizer is replaced by the content), except that the image is set to load now at low
  // priority before it is inserted: a lazy image inside the display:none holder would
  // never load.
  function hydrateParked(slide) {
    var tpl = slide.querySelector(TEMPLATE_SELECTOR);
    if (!tpl) return false;
    var img = tpl.content.querySelector('img');
    if (img) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'low');
    }
    var sizer = tpl.parentElement;
    if (sizer && sizer.hasAttribute('data-tc-defer-sizer')) {
      sizer.replaceWith(tpl.content);
    } else {
      tpl.replaceWith(tpl.content);
    }
    return true;
  }

  // Issue #484 R3: the trailing shared slides stay in the slider, so a fade slider (the
  // desktop layout, and the iPad width) still holds 2 or more slides while remove()
  // runs. flickity-fade's animate() runs synchronously inside remove()'s select() and
  // reads this.slides[fadeIndex] before its own 'select' handler clamps that index:
  // with the shopper on photo 6 of a color and 3 callouts left, it threw and the
  // slider kept the callouts alone. Clamp the fade bookkeeping to the `remaining`
  // slides first. (Before R3 every slide left the slider, and fadeSlides() returns
  // early under 2 slides.)
  function clampFade(flkty, remaining) {
    if (!flkty.options || !flkty.options.fade) return;
    var last = Math.max(0, remaining - 1);
    if (typeof flkty.fadeIndex === 'number' && flkty.fadeIndex > last) flkty.fadeIndex = last;
    if (typeof flkty.fadeHideIndex === 'number' && flkty.fadeHideIndex > last) flkty.fadeHideIndex = undefined;
  }

  function setup(gallery) {
    if (gallery.__tcColorGroupsBound) return;
    gallery.__tcColorGroupsBound = true;

    // Grouping swaps Flickity cells, so it needs a slider at BOTH breakpoints: the
    // Slides desktop layout and a phone slideshow/carousel/thumbs (theme.js reads
    // both attributes; missing = "none"). With a Stacked/Grid/Mosaic desktop layout
    // or no phone slider there is no slider to swap on one side of 768px, and a
    // resize across it would strand parked slides that theme.js then scrolls to by
    // DOM position (Codex P2s on 046fe03). Such a gallery is left ungrouped.
    var desktopStyle = gallery.getAttribute('data-slideshow-desktop-style') || 'none';
    var mobileStyle = gallery.getAttribute('data-slideshow-mobile-style') || 'none';
    if (desktopStyle === 'none' || mobileStyle === 'none') return;

    var section = gallery.closest(SECTION_SELECTOR);
    var product = readProduct(section);
    var slides = slidesIn(gallery);
    // Issue #475: server-rendered first paint when the slides carry data-tc-color (their
    // parked colors are in the holder right after the gallery), else the client path.
    var server = slides.length > 0 && slides[0].hasAttribute(COLOR_ATTR);
    var store = gallery.nextElementSibling;
    if (!store || !store.matches(STORE_SELECTOR)) store = null;
    if (server && store) slides = slides.concat(slidesIn(store));
    var groups = server ? serverGroups(slides) : buildGroups(product, slides);
    if (!groups) return;

    // shared: photos every color shows first. sharedAfter (#484 R3, server path only):
    // callouts every color shows after its own photos. byColor: each color's slides in
    // DOM order (the server renders the selected variant's own image first, #484 R2).
    var shared = [];
    var sharedAfter = [];
    var sharedAfterIds = {};
    var byColor = {};
    slides.forEach(function (slide) {
      var color = groups.colorByMediaId[slide.getAttribute('data-media-id')];
      if (color === '' && server && slide.getAttribute(SHARED_ATTR) === 'after') {
        sharedAfter.push(slide);
        sharedAfterIds[slide.getAttribute('data-media-id')] = true;
      } else if (color === '') {
        shared.push(slide);
      } else {
        (byColor[color] = byColor[color] || []).push(slide);
      }
    });
    var thumbScope = gallery.closest(MEDIA_WRAPPER_SELECTOR) || section || document;
    var thumbs = Array.prototype.slice.call(thumbScope.querySelectorAll(THUMB_SELECTOR));
    var thumbsAfter = thumbs.filter(function (thumb) { return sharedAfterIds[thumb.getAttribute('data-media-select')]; });
    // Server path: the gallery already shows this color (and the server already hid the
    // other colors' thumbnails). Client path: nothing is applied until the slider exists.
    var current = server ? gallery.getAttribute('data-tc-color-group') : null;
    if (current !== null && !byColor[current]) current = null;

    // Slides of the colors not shown wait here, hidden, still inside the product
    // section. theme.js wires zoom by querying the section for [data-zoom-button] once
    // PhotoSwipe has loaded, which can happen after the first swap; a slide parked
    // outside the document would never get its zoom listener (a11y-qa FAIL on
    // b8177da: zoom did nothing after a color change). display:none keeps the parked
    // photos out of the accessibility tree, the tab order and the image queue.
    // Since issue #475 the server renders this holder; it is created here only for the
    // client path.
    if (!store) {
      store = document.createElement('div');
      store.hidden = true;
      store.style.display = 'none';
      store.setAttribute('aria-hidden', 'true');
      store.setAttribute('data-tc-color-group-store', '');
      gallery.parentNode.insertBefore(store, gallery.nextSibling);
    }

    function colorOfMedia(id) {
      var color = groups.colorByMediaId[String(id)];
      return color === undefined ? null : color;
    }

    // Swaps the slider to `color`. Returns false when there is no live slider yet.
    function apply(color) {
      var flkty = liveFlickity(gallery);
      if (!flkty) return false;
      if (color === current) return true;
      // Leading shared, the new color, trailing shared (#484 R3): the trailing set is
      // always kept, so the new color goes in between, at index shared.length.
      var keep = shared.concat(byColor[color] || [], sharedAfter);
      // Issue #520: insert the new color first, then remove the old one, so the slider
      // is never empty. An empty slider leaves selectedIndex at -1, insert() selects the
      // new color's last slide and emits `change`, and tc-gallery-defer.js then hydrates
      // that slide's neighbors and requests its photo at high priority. With the old
      // selected slide still in place, insert() and remove() only re-index it (no
      // `change`), and selectMedia() emits the one `change` for the chosen photo.
      var present = flkty.cells.map(function (cell) { return cell.element; });
      var add = (byColor[color] || []).filter(function (el) { return present.indexOf(el) < 0; });
      if (add.length) flkty.insert(add, shared.length);
      var remove = [];
      flkty.cells.forEach(function (cell) {
        if (keep.indexOf(cell.element) < 0) remove.push(cell.element);
      });
      if (remove.length) {
        clampFade(flkty, flkty.cells.length - remove.length);
        flkty.remove(remove); // detaches them from the slider
        remove.forEach(function (el) { store.appendChild(el); });
      }
      var lastOwnThumb = null;
      thumbs.forEach(function (thumb) {
        var thumbColor = colorOfMedia(thumb.getAttribute('data-media-select'));
        thumb.style.display = thumbColor && thumbColor !== color ? 'none' : '';
        if (thumbColor === color) lastOwnThumb = thumb;
      });
      // #484 R3: the trailing shared thumbnails follow the new color's thumbnails, in the
      // same after-the-color position as their slides. Only they move, so the color
      // thumbnails keep their relative order and `thumbs` still lists them in DOM order.
      if (thumbsAfter.length && lastOwnThumb) {
        var anchor = lastOwnThumb;
        thumbsAfter.forEach(function (thumb) {
          anchor.parentNode.insertBefore(thumb, anchor.nextSibling);
          anchor = thumb;
        });
      }
      current = color;
      gallery.setAttribute('data-tc-color-group', color);
      return true;
    }

    // After a swap the slide under the selected index is a different photo even when
    // the index itself is unchanged, and Flickity only emits `change` for a new index.
    // Listeners that key off `change` (tc-gallery-defer.js hydrates the selected
    // slide there; theme.js Media updates its current slide) still need to hear it.
    function selectMedia(id, swapped) {
      var flkty = liveFlickity(gallery);
      if (!flkty) return;
      var index = indexOfMedia(flkty, id);
      if (index < 0) return;
      if (index !== flkty.selectedIndex) flkty.select(index, false, true);
      else if (swapped && typeof flkty.dispatchEvent === 'function') flkty.dispatchEvent('change', null, [index]);
    }

    function selectedVariant() {
      if (!product || !product.variants) return null;
      var input = section && section.querySelector('input[name="id"], select[name="id"]');
      var id = input && input.value;
      for (var v = 0; id && v < product.variants.length; v++) {
        if (String(product.variants[v].id) === String(id)) return product.variants[v];
      }
      return null;
    }

    // Capture phase on document: runs before theme.js's own listener on the gallery.
    document.addEventListener('theme:image:change', function (event) {
      if (event.target !== gallery || !event.detail) return;
      var color = colorOfMedia(event.detail.id);
      if (!color || color === current) return; // shared photo, or same color: theme.js handles it
      if (apply(color)) selectMedia(event.detail.id, true);
    }, true);

    // First pass once theme.js has built the slider: show the selected variant's color
    // (a ?variant= link or a sold-out first color may not start on slide 1). On the
    // server path the slider was built from that color already, so this only acts on a
    // mismatch (a browser restoring the form's variant, a change before the slider).
    var tries = 0;
    (function bind() {
      if (!liveFlickity(gallery)) {
        if (tries++ < BIND_RETRY_LIMIT) setTimeout(bind, BIND_RETRY_MS);
        return;
      }
      var variant = selectedVariant();
      var id = variant && variant.featured_media ? variant.featured_media.id : null;
      var color = id != null ? colorOfMedia(id) : null;
      if (server) {
        if (color && color !== current) {
          if (apply(color)) selectMedia(id, true);
        } else if (color && byColor[color] && byColor[color][0].getAttribute('data-media-id') !== String(id)) {
          // #484 R2: same color, another combination than the server painted (a bundle
          // whose form state the browser restored): select that combination's photo.
          selectMedia(id, false);
        }
        return;
      }
      if (!color) color = groups.colors[0];
      if (apply(color) && id != null && colorOfMedia(id) === color) selectMedia(id, true);
    })();

    // Issue #520: the slide a tap on `color` would show. With more than one option (the
    // R2 bundles) that is the photo of the variant with this color and the chosen
    // variant's other option values. The color's first photo (#475) when that
    // combination does not exist, is sold out or has no slide of this color, and always
    // on a single-option product.
    var multiOption = !!(product && product.options && product.options.length > 1);
    function nextTapSlide(color, chosen) {
      var lead = byColor[color][0];
      var colorIndex = colorOptionIndex(product);
      if (!chosen || colorIndex < 0 || !multiOption) return lead;
      var match = null;
      for (var v = 0; v < product.variants.length && !match; v++) {
        var variant = product.variants[v];
        if (optionValue(variant, colorIndex) !== color) continue;
        var same = true;
        for (var o = 0; o < product.options.length && same; o++) {
          if (o !== colorIndex && optionValue(variant, o) !== optionValue(chosen, o)) same = false;
        }
        if (same) match = variant;
      }
      if (!match || !match.available || !match.featured_media) return lead;
      var id = String(match.featured_media.id);
      for (var s = 0; s < byColor[color].length; s++) {
        if (byColor[color][s].getAttribute('data-media-id') === id) return byColor[color][s];
      }
      return lead;
    }

    // Issue #475 preload: after `load` and an idle moment, the photo a tap on each other
    // color would show (#520; the lead photo, the first slide of its block, on
    // single-option products) downloads into its parked slide, so a swatch tap shows it
    // at once instead of a blank slide. Only slides that are actually parked, only the
    // main gallery (the sticky bar's copy would request the same files again), never on
    // Save-Data or a 2g connection, and (#484) only colors with at least one available
    // variant. pendingNextTaps() lists those slides whose photo still has to download:
    // at most one per color for the option state it reads.
    function pendingNextTaps() {
      var pending = [];
      if (saveDataRequested()) return pending;
      var available = availableColors(product);
      var chosen = selectedVariant();
      Object.keys(byColor).forEach(function (color) {
        if (color === current) return;
        if (available && !available[color]) return; // #484: sold-out colors are not preloaded
        var slide = nextTapSlide(color, chosen);
        if (slide && store.contains(slide) && needsDownload(slide)) pending.push(slide);
      });
      return pending;
    }
    // A parked slide still to download: a deferred template, or (#520, multi-option
    // products only) a photo hydrated earlier as a neighbor of a viewed slide whose lazy
    // image never loaded and never will inside the display:none holder. On a phone the
    // slider keeps such neighbors off screen, so after a second-option change the next
    // color tap would otherwise start that photo only when it is shown.
    function needsDownload(slide) {
      if (slide.querySelector(TEMPLATE_SELECTOR)) return true;
      var img = multiOption ? slide.querySelector('img') : null;
      return !!(img && !img.complete && img.getAttribute('loading') === 'lazy');
    }
    // Starts that download at low priority while the slide stays parked.
    function preloadParked(slide) {
      if (hydrateParked(slide)) return;
      var img = slide.querySelector('img');
      if (img && !img.complete && img.getAttribute('loading') === 'lazy') {
        img.setAttribute('fetchpriority', 'low');
        img.setAttribute('loading', 'eager');
      }
    }
    function whenIdle(fn) {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fn, { timeout: PRELOAD_IDLE_TIMEOUT_MS });
      } else {
        setTimeout(fn, PRELOAD_FALLBACK_MS);
      }
    }
    if (!gallery.closest(STICKY_COPY_SELECTOR)) {
      var preloadArmed = false;
      var warmed = {}; // media id -> detached Image warming the cache for an in-slider template (#526, #533)
      // One in-slider step; returns the image to wait for. Downloads go through a
      // detached Image (low priority, never boosted as "in viewport", no DOM change).
      // Once that file is in the browser cache the slide's template is swapped in, the
      // same swap as hydrateSlide() in tc-gallery-defer.js (the aspect-ratio sizer is
      // replaced by content of the same box; measured at 375 and 1280: no cell, slider
      // or scroll change, no layout shift), so the theme's 300 ms fade-in of a new image
      // runs while the slide is not shown, and a second-option tap paints the photo at
      // once (49 ms at 1280 against 181 ms when the swap waited for the tap).
      // Shared since issue #533: the multi-option trickle (#526) and the single-option
      // opening-lead warm below both use it.
      var warmInSlider = function (slide) {
        var tpl = slide.querySelector(TEMPLATE_SELECTOR);
        var done = warmed[slide.getAttribute('data-media-id')];
        if (done && done !== true && done.complete) {
          hydrateParked(slide); // from the browser cache: no second request
          return slide.querySelector('img');
        }
        var source = tpl.content.querySelector('img');
        var warm = null;
        if (source && (source.getAttribute('srcset') || source.getAttribute('src'))) {
          warm = new Image();
          warm.setAttribute('fetchpriority', 'low');
          if (source.getAttribute('sizes')) warm.setAttribute('sizes', source.getAttribute('sizes'));
          if (source.getAttribute('srcset')) warm.setAttribute('srcset', source.getAttribute('srcset'));
          if (source.getAttribute('src')) warm.setAttribute('src', source.getAttribute('src'));
        }
        warmed[slide.getAttribute('data-media-id')] = warm || true;
        return warm;
      };
      // Calls fn once every image in `imgs` has loaded or failed (null and finished
      // images count as done).
      var whenLoaded = function (imgs, fn) {
        var left = 0;
        var finish = function () { if (--left === 0) fn(); };
        imgs.forEach(function (img) {
          if (!img || img.complete) return;
          left++;
          var settle = function () {
            img.removeEventListener('load', settle);
            img.removeEventListener('error', settle);
            finish();
          };
          img.addEventListener('load', settle);
          img.addEventListener('error', settle);
        });
        if (left === 0) fn();
      };
      // Issue #533 (single-option products only; multi-option products keep the #526
      // trickle unchanged): a gallery that opens on LEADING shared photos (Argo Pet: 7
      // on Pawsport, 4 on Tails) shows its own color's lead photo after them, at slider
      // position 8 or 5, outside tc-gallery-defer.js's +-2 window. That slide keeps its
      // deferred template, the post-load pass above only preloads OTHER colors, and a
      // swap parks it undownloaded: tap Black, then Beige again, and Beige's lead
      // downloaded on demand (143-164 blank frames at 1.6 Mbps on Tails). This warms the
      // OPENING color's lead: the color the gallery shows when the post-load pass is set
      // up, captured once there, before any wait. Codex P2 on PR #535 (review comment
      // 4187046353): reading `current` when the warm finally runs missed a shopper who
      // changed color while the preloads were still downloading (seconds at 1.6 Mbps);
      // the warm then exited for the new color and the opening lead stayed parked
      // undownloaded. So the captured slide is warmed wherever it is by then:
      // - still a deferred template in the slider (not the selected slide): warmInSlider(),
      //   a detached Image, then the template swapped in from the browser cache;
      // - parked in the holder, still a template: preloadParked(), the same eager,
      //   low-priority download the other colors' leads get;
      // - already hydrated or downloaded, or a sold-out opening color: nothing.
      // Once per page, never re-armed by later color changes; never the selected slide,
      // main gallery only, never on Save-Data or 2g, and only after the shown photo and
      // the other colors' preloads have finished, so it competes with neither. Products
      // without leading shared photos open on their lead (the selected slide 1), so
      // nothing changes there.
      var openingLeadToWarm = function (lead) {
        var flkty = liveFlickity(gallery);
        if (!lead || !flkty || saveDataRequested()) return null;
        var available = availableColors(product);
        var color = colorOfMedia(lead.getAttribute('data-media-id'));
        if (!color || (available && !available[color])) return null;
        if (lead === flkty.selectedElement || !lead.querySelector(TEMPLATE_SELECTOR)) return null;
        if (warmed[lead.getAttribute('data-media-id')]) return null; // started once already
        return lead;
      };
      var warmOpeningLead = function (captured) {
        var lead = openingLeadToWarm(captured);
        if (!lead) return;
        var flkty = liveFlickity(gallery);
        var shown = flkty && flkty.selectedElement ? flkty.selectedElement.querySelector('img') : null;
        if (shown && !shown.complete) {
          // the shopper may have moved to a photo that is still loading: wait for it
          whenLoaded([shown], function () { whenIdle(function () { warmOpeningLead(captured); }); });
          return;
        }
        if (store.contains(lead)) {
          preloadParked(lead); // parked: download it in the holder, ready for the return
          return;
        }
        if (indexOfMedia(flkty, lead.getAttribute('data-media-id')) < 0) return;
        var warm = warmInSlider(lead);
        if (!warm) return;
        whenLoaded([warm], function () {
          whenIdle(function () {
            // swap the template in from the cache (wherever the slide is now) unless
            // something else hydrated it
            if (lead.querySelector(TEMPLATE_SELECTOR)) warmInSlider(lead);
          });
        });
      };
      var firstPass = function () {
        preloadArmed = true;
        // #533: the opening color's lead, captured before any wait (Codex P2 on #535)
        var openingLead = !multiOption && current && byColor[current] ? byColor[current][0] : null;
        whenIdle(function () {
          var started = pendingNextTaps();
          started.forEach(preloadParked);
          if (!openingLead) return;
          whenLoaded(started.map(function (slide) { return slide.querySelector('img'); }), function () {
            whenIdle(function () { warmOpeningLead(openingLead); });
          });
        });
      };
      if (document.readyState === 'complete') firstPass();
      else window.addEventListener('load', firstPass);

      // #520 / #526: on a multi-option product every variant change re-runs the trickle
      // (header, TRICKLE). theme.js fires theme:variant:change, bubbling, from the product
      // section after every option change, once the gallery has selected (and
      // tc-gallery-defer.js has started) the new photo. One download at a time: started
      // all at once, four combination photos (about 450 KB) shared a 1.6 Mbps link with
      // the next photo the shopper picked, which then took 4.1 s instead of under 1 s
      // (MaxPorter, 1280). Each step reads the chosen variant when it runs, so taps in
      // quick succession add at most one photo per passing state.
      if (section && multiOption) {
        var waitingOn = null; // the one image the trickle waits for: the shown photo or its own last download
        var stepQueued = false;
        var shownImage = function () {
          var flkty = liveFlickity(gallery);
          return flkty && flkty.selectedElement ? flkty.selectedElement.querySelector('img') : null;
        };
        // #526: the current color's combination photos (featured images of its in-stock
        // variants) whose slides still hold their deferred template, nearest first.
        // Hydrated slides are left to tc-gallery-defer.js: it hydrates the +-2 window
        // around the selected slide and those images load at once (fade stack) or within
        // the lazy margin (phone slider). Waiting on one of them stalled the trickle for
        // 9 s behind a neighbor at 1.6 Mbps, and currentSrc cannot tell an image that is
        // already downloading from one that never will (both measured).
        var currentColorPending = function () {
          var flkty = liveFlickity(gallery);
          var colorIndex = colorOptionIndex(product);
          if (!flkty || !current || colorIndex < 0 || saveDataRequested()) return [];
          var combos = {};
          product.variants.forEach(function (variant) {
            if (variant.available && variant.featured_media && optionValue(variant, colorIndex) === current) {
              combos[String(variant.featured_media.id)] = true;
            }
          });
          var n = flkty.cells.length;
          var sel = flkty.selectedIndex;
          var ranked = [];
          for (var i = 0; i < n; i++) {
            var slide = flkty.cells[i].element;
            var id = slide.getAttribute('data-media-id');
            if (i === sel || !combos[id]) continue;
            if (!slide.querySelector(TEMPLATE_SELECTOR)) continue;
            var warm = warmed[id];
            // not warmed yet, or warmed and downloaded (then it is swapped in)
            if (warm && (warm === true || !warm.complete)) continue;
            var ahead = ((i - sel) % n + n) % n;
            var behind = n - ahead;
            ranked.push({ slide: slide, rank: Math.min(ahead, behind) * 2 + (ahead <= behind ? 0 : 1) });
          }
          ranked.sort(function (a, b) { return a.rank - b.rank; });
          return ranked.map(function (r) { return r.slide; });
        };
        // In-slider steps use warmInSlider() above (shared with the #533 warm).
        var waitFor = function (img) {
          if (img === waitingOn) return; // never a second pair of listeners on the same image
          waitingOn = img;
          var resume = function () {
            img.removeEventListener('load', resume);
            img.removeEventListener('error', resume);
            if (waitingOn === img) waitingOn = null;
            trickle(); // the shopper may have moved on to another photo meanwhile
          };
          img.addEventListener('load', resume);
          img.addEventListener('error', resume);
        };
        // True when the trickle may start a download now; otherwise it waits for the
        // image that blocks it, whose load or error event resumes the trickle.
        var mayStart = function () {
          if (waitingOn) return false;
          var shown = shownImage();
          if (shown && !shown.complete) {
            waitFor(shown);
            return false;
          }
          return true;
        };
        var trickle = function () {
          if (!mayStart() || stepQueued) return;
          stepQueued = true;
          whenIdle(function () {
            stepQueued = false;
            // D1 (#525 independent QA): a variant change can land between queueing this
            // step and running it, so check again before starting anything.
            if (!mayStart()) return;
            var img = null;
            var inSlider = currentColorPending()[0];
            if (inSlider) {
              img = warmInSlider(inSlider);
            } else {
              var parked = pendingNextTaps()[0];
              if (!parked) return; // everything is downloaded: the trickle ends
              preloadParked(parked);
              img = parked.querySelector('img');
            }
            if (img && !img.complete) waitFor(img);
            else trickle();
          });
        };
        section.addEventListener('theme:variant:change', function () {
          if (preloadArmed) trickle();
        });
      }
    }
  }

  function scan(root) {
    (root || document).querySelectorAll(GALLERY_SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) scan(e.target);
  });

  window.tcGalleryColorGroups = { scan: scan, buildGroups: buildGroups };
})();
